// Updated RawInspectionsTab.jsx with configuration integration

import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
// import inferenceService from '../../../js/Inspection/services/inferenceService.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';

// Import the new reusable components
import {
    InspectionTable,
    InspectionDetailsModal,
    ImageModal
} from '../components/InspectionComponents.jsx';

/**
 * Main component for displaying a paginated table of raw inspection data with configuration integration.
 */
export const RawInspectionsTab = () => {
    const appContext = useAppContext();
    const { state, setLoading, setError, addNotification, withManualImagesPref } = appContext;

    // Extract configuration preferences
    const {
        itemsPerPage,
        show_confidence_scores,
        includeManualImages,
        default_date_range,
        autoRefresh,
        refreshInterval
    } = state.preferences;

    console.log('[RawInspectionsTab] Configuration preferences:', {
        itemsPerPage,
        show_confidence_scores,
        includeManualImages,
        default_date_range,
        autoRefresh,
        refreshInterval
    });

    const [inspections, setInspections] = useState([]);
    const [modelList, setModelList] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: itemsPerPage || 20, // Use configured items per page
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
    });
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [largeImage, setLargeImage] = useState(null);

    // Update pagination limit when itemsPerPage preference changes
    useEffect(() => {
        if (itemsPerPage && itemsPerPage !== pagination.limit) {
            console.log('[RawInspectionsTab] Updating pagination limit to:', itemsPerPage);
            setPagination(prev => ({ ...prev, limit: itemsPerPage, page: 1 }));
        }
    }, [itemsPerPage, pagination.limit]);

    // Load model list for display names
    useEffect(() => {
        const fetchModelList = async () => {
            try {
                console.log('[RawInspectionsTab] Loading model list for display names');
                // const models = await inferenceService.fetchAvailableModels(null);
                const models = await api.getAvailableModels(null);
                setModelList(models || []);
            } catch (error) {
                console.error("[RawInspectionsTab] Failed to fetch model list:", error);
                if (addNotification) {
                    addNotification({ type: 'warning', message: 'Could not load model names.' });
                }
            }
        };
        fetchModelList();
    }, [addNotification]);

    // Apply configuration preferences to filters
    const getConfiguredFilters = useCallback(() => {
        const baseFilters = state.filters || {};
        
        // Apply manual images preference using the context helper
        const configuredFilters = withManualImagesPref(baseFilters);
        
        // Apply default date range if not specified
        if (!configuredFilters.dateRange && default_date_range) {
            configuredFilters.dateRange = default_date_range;
            console.log('[RawInspectionsTab] Applied default date range:', default_date_range);
        }
        
        console.log('[RawInspectionsTab] Configured filters:', configuredFilters);
        return configuredFilters;
    }, [state.filters, withManualImagesPref, default_date_range]);
    
    const { page, limit } = pagination;

    // Load inspections when filters or pagination change, with configuration applied
    const loadInspections = useCallback(async () => {
        try {
            console.log('[RawInspectionsTab] Loading inspections with configuration...');
            setLoading(true);
            setError(null);

            const configuredFilters = getConfiguredFilters();

            console.log('[RawInspectionsTab] API filters with config:', configuredFilters);
            console.log('[RawInspectionsTab] Pagination:', { page, limit });

            const response = await api.getInspections(configuredFilters, page, limit);
            console.log('[RawInspectionsTab] Inspections response:', response);

            if (response && response.data) {
                setInspections(response.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || 0,
                    total_pages: response.total_pages || 1,
                    page: response.page || prev.page,
                    has_next: response.has_next || false,
                    has_prev: response.has_prev || false
                }));
            } else {
                setInspections([]);
                setPagination(prev => ({
                    ...prev, total: 0, total_pages: 1, has_next: false, has_prev: false
                }));
            }

            // Show configuration-aware notifications
            if (response?.data?.length === 0 && includeManualImages === false) {
                addNotification({
                    type: 'info',
                    message: 'No inspections found. Manual images are excluded - check Configuration tab to include them.',
                    duration: 5000
                });
            }

        } catch (error) {
            console.error('[RawInspectionsTab] Error loading inspections:', error);
            setError(error);
            if (addNotification) {
                addNotification({
                    type: 'danger',
                    message: `Failed to load inspections: ${error.message}`
                });
            }
        } finally {
            setLoading(false);
        }
    }, [
        page, limit, getConfiguredFilters, setLoading, setError, addNotification, includeManualImages
    ]);

    useEffect(() => {
        loadInspections();
    }, [loadInspections]);

    // Auto-refresh functionality based on configuration
    useEffect(() => {
        if (!autoRefresh || !refreshInterval) return;

        console.log('[RawInspectionsTab] Setting up auto-refresh with interval:', refreshInterval);
        const intervalId = setInterval(() => {
            console.log('[RawInspectionsTab] Auto-refreshing data...');
            loadInspections();
        }, refreshInterval * 1000);

        return () => {
            console.log('[RawInspectionsTab] Clearing auto-refresh interval');
            clearInterval(intervalId);
        };
    }, [autoRefresh, refreshInterval, loadInspections]);

    // Pagination handlers
    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= pagination.total_pages && newPage !== pagination.page) {
            console.log('[RawInspectionsTab] Changing to page:', newPage);
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    }, [pagination.page, pagination.total_pages]);

    // Modal handlers
    const handleViewDetails = useCallback((inspection) => {
        setSelectedInspection(inspection);
        setShowDetails(true);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setShowDetails(false);
        setSelectedInspection(null);
    }, []);

    const handleImageClick = useCallback((imageUrl) => {
        setLargeImage(imageUrl);
    }, []);

    const handleCloseLargeImage = useCallback(() => {
        setLargeImage(null);
    }, []);

    // Ground truth handling
    const handleSetGroundTruth = useCallback(async (inspectionId, status) => {
        try {
            setLoading(true);
            const data = await api.confirmClassification(inspectionId, status);
            const confirmedAt = new Date().toISOString();
            const confirmedBy = data?.username || 'current user';

            const updateInspection = (insp) => ({
                ...insp,
                confirmation_status: status,
                confirmation_timestamp: confirmedAt,
                confirmed_by: confirmedBy
            });

            setInspections(prev => prev.map(insp =>
                insp._id === inspectionId ? updateInspection(insp) : insp
            ));

            if (selectedInspection?._id === inspectionId) {
                setSelectedInspection(prev => updateInspection(prev));
            }

            if (addNotification) {
                addNotification({ type: 'success', message: `Ground truth set as ${status}` });
            }
        } catch (error) {
            if (addNotification) {
                addNotification({ type: 'danger', message: `Failed to update: ${error.message}` });
            }
        } finally {
            setLoading(false);
        }
    }, [selectedInspection, setLoading, addNotification]);

    // Handle manual refresh
    const handleRefresh = useCallback(() => {
        console.log('[RawInspectionsTab] Manual refresh triggered');
        loadInspections();
    }, [loadInspections]);

    const currentFilters = getConfiguredFilters();

    if (state.loading && !inspections.length) {
        return <LoadingSpinner message="Loading inspections..." size="large" />;
    }

    return (
        <ErrorBoundary>
            <div className="inspections-content fade-in">
                {/* Configuration Status Header */}
                <div className="card shadow-sm mb-3">
                    <div className="card-body py-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-cog text-primary me-2"></i>
                                <small className="text-muted">
                                    Configuration: {itemsPerPage || 20} per page •
                                    Manual images: {includeManualImages ? 'included' : 'excluded'} •
                                    Confidence scores: {show_confidence_scores ? 'shown' : 'hidden'}
                                    {autoRefresh && ` • Auto-refresh: ${refreshInterval}s`}
                                </small>
                            </div>
                            <div className="d-flex align-items-center">
                                {autoRefresh && (
                                    <div className="me-3">
                                        <i className="fas fa-sync-alt fa-spin text-success me-1"></i>
                                        <small className="text-success">Auto-refreshing</small>
                                    </div>
                                )}
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleRefresh}
                                    disabled={state.loading}
                                    title="Refresh data"
                                >
                                    <i className={`fas fa-sync-alt ${state.loading ? 'fa-spin' : ''}`}></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <InspectionTable
                    inspections={inspections}
                    onViewDetails={handleViewDetails}
                    onImageClick={handleImageClick}
                    onSetGroundTruth={handleSetGroundTruth}
                    isLoading={state.loading}
                    filters={currentFilters}
                    showExportButtons={true}
                    addNotification={addNotification}
                />

                {pagination.total_pages > 1 && (
                    <div className="card shadow-sm mt-3">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="text-muted small">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {
                                        Math.min(pagination.page * pagination.limit, pagination.total)
                                    } of {pagination.total.toLocaleString()}
                                    {includeManualImages === false && (
                                        <span className="text-info ms-2">
                                            <i className="fas fa-info-circle me-1"></i>
                                            Manual images excluded
                                        </span>
                                    )}
                                </div>
                                <nav>
                                    <ul className="pagination pagination-sm mb-0">
                                        <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(1)} disabled={!pagination.has_prev}>&laquo;</button>
                                        </li>
                                        <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(pagination.page - 1)} disabled={!pagination.has_prev}>&lsaquo;</button>
                                        </li>
                                        <li className="page-item active">
                                            <span className="page-link">{pagination.page} of {pagination.total_pages}</span>
                                        </li>
                                        <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(pagination.page + 1)} disabled={!pagination.has_next}>&rsaquo;</button>
                                        </li>
                                        <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(pagination.total_pages)} disabled={!pagination.has_next}>&raquo;</button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}

                {showDetails && selectedInspection && (
                    <InspectionDetailsModal
                        inspection={selectedInspection}
                        modelList={modelList}
                        onClose={handleCloseDetails}
                        onImageClick={handleImageClick}
                        onSetGroundTruth={handleSetGroundTruth}
                        isLoading={state.loading}
                        addNotification={addNotification}
                    />
                )}

                <ImageModal
                    src={largeImage}
                    alt="Enlarged view"
                    show={!!largeImage}
                    onClose={handleCloseLargeImage}
                />
            </div>
        </ErrorBoundary>
    );
};